import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";

/* ─────────────────────────────────────────────────────────────
   HERO IMAGE — paste your Cloudinary URL here.
   Leave empty to show the labelled placeholder.
   ───────────────────────────────────────────────────────────── */
const HERO_IMAGE = "https://res.cloudinary.com/dmrlotfsv/image/upload/t_VESHH-EON_WEBSITE/f_auto/q_auto/OverSized_Terry_check_ex1rf7.png";
const HERO_ALT = "Black Terry Oversized Tee, worn by a model in a studio";

/* ANATOMY IMAGE — replaces the drawn tee diagram. Leave empty to use the drawn diagram. */
const ANATOMY_IMAGE =
  "https://res.cloudinary.com/dmrlotfsv/image/upload/t_VESHH-EON_WEBSITE/f_auto/q_auto/Terry_OverSized_Tee_Prod_Spec_oubwao.png";
const ANATOMY_ALT =
  "Terry Oversized Tee anatomy: lycra ribbed neck, drop shoulder, pre-shrunk body and looped inner terry";
// Numbered squares laid over the image. Keep false if the image has its own callouts.
const SHOW_MARKERS = false;
const COLLECTION_PATH = "/collections/OverSized TShirt";

const MARQUEE = [
  "260 GSM",
  "90% COTTON · 10% POLYESTER",
  "UNISEX STYLE",
  "PRE-SHRUNK",
  "DROP SHOULDER",
  "LOOPED INNERS",
  "LYCRA RIBBED NECK",
];

// Weight scale — gsm values from THE BLANK range
const SCALE_MIN = 140;
const SCALE_MAX = 280;
const SCALE_TOP = 260; // longest bar on mobile
const SCALE = [
  { gsm: 160, label: "SUPIMA TEE" },
  { gsm: 180, label: "RINGER · RAGLAN" },
  { gsm: 220, label: "POLO TEE" },
  { gsm: 240, label: "STRETCH TEE" },
  { gsm: 260, label: "TERRY OVERSIZED — THIS TEE", active: true },
];
const scalePos = (gsm) => ((gsm - SCALE_MIN) / (SCALE_MAX - SCALE_MIN)) * 100;

// Anatomy — marker positions are % of the diagram box
const ANATOMY = [
  {
    n: "01",
    title: "Lycra ribbed neck",
    text: "Stretches over your head, then snaps right back. The neckline stays clean and in shape, wash after wash.",
    left: 50,
    top: 15,
  },
  {
    n: "02",
    title: "Drop shoulder",
    text: "The seam falls past your shoulder, so the sleeves hang wide and easy. Instant relaxed shape, on any build.",
    left: 24,
    top: 30,
  },
  {
    n: "03",
    title: "Pre-shrunk",
    text: "Already shrunk before it reaches you. The size you choose is the fit you keep, with no surprises after the first wash.",
    left: 50,
    top: 56,
  },
  {
    n: "04",
    title: "Looped inners",
    text: "Soft terry loops line the inside. Against your skin it feels calm, cushioned and easy to stay in all day.",
    left: 50,
    top: 80,
  },
];

const OtEyebrow = ({ children, invert = false }) => (
  <div className={`ot-eyebrow${invert ? " ot-eyebrow--invert" : ""}`}>
    <span className="ot-eyebrow__line" aria-hidden="true" />
    <span className="ot-eyebrow__text">{children}</span>
  </div>
);

const FeaturedOverSizedShirt = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const res = await axios.get(
          `${import.meta.env.VITE_BACKEND_URL}/api/products`,
          { params: { collection: "OverSized T-Shirt" } }
        );
        setProducts(res.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const displayProducts = products.slice(0, 8);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Exo+2:wght@200;300;400;500;600;700;800&display=swap');

        /* ══════════════════════════════════════════════
           OVERSIZED TEE — HERO + SPEC STORY  (prefix: ot-)
           ══════════════════════════════════════════════ */
        .ot-root {
          --ot-ink: #0a0a0a;
          --ot-white: #ffffff;
          --ot-mist: #f8f8f8;
          --ot-line: #d8d8d8;
          --ot-muted: #4a4a4a;
          --ot-pad: 48px;
          font-family: 'Exo 2', sans-serif;
          color: var(--ot-ink);
          background: var(--ot-white);
          width: 100%;
          overflow-x: hidden;
        }
        .ot-root *, .ot-root *::before, .ot-root *::after {
          box-sizing: border-box;
          border-radius: 0;
        }

        /* shared */
        .ot-eyebrow { display: flex; align-items: center; gap: 14px; }
        .ot-eyebrow__line { display: block; flex: 0 0 32px; width: 32px; height: 1px; background: var(--ot-ink); }
        .ot-eyebrow__text {
          font-size: 11px; font-weight: 600; letter-spacing: 0.34em; text-transform: uppercase;
        }
        .ot-eyebrow--invert .ot-eyebrow__line { background: var(--ot-white); }

        .ot-btn {
          display: inline-flex; align-items: center; justify-content: center;
          min-height: 52px; padding: 0 36px;
          border: 1px solid var(--ot-ink);
          text-decoration: none; font-family: inherit;
          font-size: 12px; font-weight: 600; letter-spacing: 0.3em; text-transform: uppercase;
          cursor: pointer;
          transition: background-color 0.25s ease, color 0.25s ease;
        }
        .ot-btn--dark { background: var(--ot-ink); color: var(--ot-white); }
        .ot-btn--dark:hover { background: var(--ot-white); color: var(--ot-ink); }
        .ot-btn--light {
          width: 100%; min-height: 56px;
          background: var(--ot-white); color: var(--ot-ink); border-color: var(--ot-white);
          font-weight: 700;
        }
        .ot-btn--light:hover { background: var(--ot-ink); color: var(--ot-white); }
        .ot-btn:focus-visible { outline: 2px solid currentColor; outline-offset: 4px; }

        /* hero */
        .ot-hero { background: var(--ot-mist); overflow: hidden; }
        .ot-hero__inner { max-width: 1280px; margin: 0 auto; padding: 40px var(--ot-pad) 0; }
        .ot-hero__meta {
          display: flex; flex-wrap: wrap; justify-content: space-between; align-items: center; gap: 16px;
        }
        .ot-hero__meta-right {
          font-size: 11px; font-weight: 500; letter-spacing: 0.34em; color: var(--ot-muted);
        }
        .ot-hero__grid { display: flex; flex-wrap: wrap; align-items: stretch; margin-top: 24px; }
        .ot-hero__copy {
          position: relative; z-index: 2;
          flex: 0 1 620px; min-width: 0;
          display: flex; flex-direction: column; justify-content: space-between;
          gap: 48px; padding: 24px 0 56px;
        }
        .ot-hero__title {
          margin: 0; font-weight: 800;
          font-size: clamp(84px, 13vw, 188px);
          line-height: 0.84; letter-spacing: -0.05em; text-transform: uppercase;
        }
        .ot-hero__title span { display: block; }
        .ot-hero__cta-block { display: flex; flex-direction: column; gap: 28px; max-width: 440px; }
        .ot-hero__line { margin: 0; font-size: 26px; line-height: 1.3; font-weight: 300; }
        .ot-hero__media {
          position: relative; z-index: 1;
          flex: 1 1 440px; min-width: 0; min-height: 680px;
          background: #e6e6e6; border: 1px solid #cfcfcf; overflow: hidden;
        }
        .ot-hero__img {
          position: absolute; inset: 0; width: 100%; height: 100%;
          object-fit: cover; object-position: center top; display: block;
        }
        .ot-hero__placeholder {
          position: absolute; inset: 0;
          display: flex; flex-direction: column; align-items: center; justify-content: center;
          gap: 10px; padding: 32px; text-align: center;
        }
        .ot-hero__ph-tag { font-size: 12px; font-weight: 600; letter-spacing: 0.28em; color: #2a2a2a; }
        .ot-hero__ph-note { max-width: 300px; font-size: 14px; line-height: 1.5; color: #3a3a3a; }
        .ot-hero__fig {
          position: absolute; bottom: 18px;
          font-size: 11px; font-weight: 500; letter-spacing: 0.24em;
          color: var(--ot-ink); background: var(--ot-mist); padding: 4px 8px;
        }
        .ot-hero__fig--l { left: 20px; }
        .ot-hero__fig--r { right: 20px; }

        /* strip */
        .ot-strip { background: var(--ot-ink); color: var(--ot-white); }
        .ot-strip__inner {
          max-width: 1280px; margin: 0 auto; padding: 22px var(--ot-pad);
          list-style: none;
          display: flex; flex-wrap: wrap; justify-content: space-between; align-items: center;
          gap: 16px 20px;
        }
        .ot-strip__item {
          display: flex; align-items: center; gap: 20px;
          font-size: 11px; font-weight: 600; letter-spacing: 0.2em;
        }
        .ot-strip__dot { display: block; width: 6px; height: 6px; background: var(--ot-white); }

        /* statement */
        .ot-statement__inner {
          max-width: 1280px; margin: 0 auto; padding: 128px var(--ot-pad) 96px;
          display: flex; flex-wrap: wrap; gap: 40px 96px;
        }
        .ot-statement__label { flex: 0 0 220px; padding-top: 14px; }
        .ot-statement__body { flex: 1 1 520px; min-width: 0; }
        .ot-statement__big {
          margin: 0; font-size: clamp(30px, 3.6vw, 50px);
          line-height: 1.18; font-weight: 300; letter-spacing: -0.02em;
        }
        .ot-statement__small {
          margin: 32px 0 0; max-width: 560px;
          font-size: 17px; line-height: 1.65; color: var(--ot-muted);
        }

        /* bento */
        .ot-bento__inner {
          max-width: 1280px; margin: 0 auto; padding: 0 var(--ot-pad) 16px;
          display: flex; flex-wrap: wrap; gap: 16px;
        }
        .ot-tile {
          min-width: 0; min-height: 480px; padding: 40px;
          display: flex; flex-direction: column; justify-content: space-between; gap: 32px;
        }
        .ot-tile--ink { background: var(--ot-ink); color: var(--ot-white); }
        .ot-tile--mist { background: var(--ot-mist); }
        .ot-tile--weight { flex: 1.35 1 520px; }
        .ot-tile--mix { flex: 1 1 400px; }
        .ot-tile__copy { margin: 0; max-width: 440px; font-size: 18px; line-height: 1.55; font-weight: 300; }
        .ot-weight { display: flex; align-items: flex-end; flex-wrap: wrap; gap: 20px; }
        .ot-weight__num {
          font-size: clamp(140px, 19vw, 260px); font-weight: 800;
          line-height: 0.78; letter-spacing: -0.06em;
        }
        .ot-weight__unit { padding-bottom: 8px; font-size: 28px; font-weight: 300; letter-spacing: 0.2em; }
        .ot-mix { display: flex; flex-direction: column; gap: 20px; }
        .ot-mix__num {
          font-size: clamp(72px, 9vw, 128px); font-weight: 800;
          line-height: 0.85; letter-spacing: -0.05em;
        }
        .ot-mix__bar { display: flex; width: 100%; height: 28px; }
        .ot-mix__cotton { flex: 90 1 0; background: var(--ot-ink); }
        .ot-mix__poly { flex: 10 1 0; background: #bdbdbd; }
        .ot-mix__labels {
          display: flex; justify-content: space-between; flex-wrap: wrap; gap: 16px;
          font-size: 12px; font-weight: 600; letter-spacing: 0.22em;
        }
        .ot-mix__labels-muted { color: var(--ot-muted); }

        /* weight scale — desktop ruler */
        .ot-scale__inner { max-width: 1280px; margin: 0 auto; padding: 0 var(--ot-pad) 16px; }
        .ot-scale__panel { background: var(--ot-mist); padding: 40px; }
        .ot-scale__scroll { margin-top: 56px; overflow-x: auto; }
        .ot-scale__track { position: relative; min-width: 880px; height: 150px; }
        .ot-scale__axis { position: absolute; left: 0; right: 0; top: 20px; height: 1px; background: var(--ot-ink); }
        .ot-scale__tick {
          position: absolute; top: 0; transform: translateX(-50%);
          display: flex; flex-direction: column; align-items: center; gap: 12px;
        }
        .ot-scale__stem { display: block; width: 1px; height: 41px; background: var(--ot-ink); }
        .ot-scale__gsm { font-size: 28px; font-weight: 300; line-height: 1; letter-spacing: -0.02em; }
        .ot-scale__name {
          font-size: 11px; font-weight: 500; letter-spacing: 0.22em;
          text-align: center; color: var(--ot-muted); white-space: nowrap;
        }
        .ot-scale__tick--active .ot-scale__marker {
          display: block; width: 14px; height: 14px; margin-top: 13px; background: var(--ot-ink);
        }
        .ot-scale__tick--active .ot-scale__stem { height: 20px; margin-top: -6px; }
        .ot-scale__tick--active .ot-scale__gsm { font-size: 44px; font-weight: 800; letter-spacing: -0.03em; }
        .ot-scale__tick--active .ot-scale__name { font-weight: 700; color: var(--ot-ink); }

        /* weight scale — mobile bars (hidden on desktop) */
        .ot-bars { display: none; margin: 0; padding: 0; list-style: none; }
        .ot-bar__top {
          display: flex; justify-content: space-between; align-items: baseline; gap: 12px;
          font-size: 10px; font-weight: 500; letter-spacing: 0.16em; color: var(--ot-muted);
        }
        .ot-bar__gsm { font-size: 14px; font-weight: 400; letter-spacing: 0; color: var(--ot-ink); }
        .ot-bar__track { height: 8px; margin-top: 6px; background: #e6e6e6; }
        .ot-bar__fill { height: 100%; background: #bdbdbd; }
        .ot-bar--active .ot-bar__top { font-weight: 700; color: var(--ot-ink); }
        .ot-bar--active .ot-bar__gsm { font-weight: 800; }
        .ot-bar--active .ot-bar__fill { background: var(--ot-ink); }

        /* anatomy */
        .ot-anatomy__inner { max-width: 1280px; margin: 0 auto; padding: 112px var(--ot-pad) 120px; }
        .ot-anatomy__title {
          margin: 24px 0 72px; max-width: 900px;
          font-size: clamp(44px, 6vw, 88px); font-weight: 800;
          line-height: 0.95; letter-spacing: -0.04em; text-transform: uppercase;
        }
        .ot-anatomy__grid { display: flex; flex-wrap: wrap; align-items: flex-start; gap: 48px 80px; }
        .ot-diagram {
          position: relative; flex: 1 1 440px; max-width: 560px; min-width: 0;
          aspect-ratio: 1 / 1; padding: 6%; background: var(--ot-mist);
        }
        .ot-diagram__svg { display: block; width: 100%; height: 100%; }
        .ot-diagram__marker {
          position: absolute; transform: translate(-50%, -50%);
          width: 32px; height: 32px;
          display: flex; align-items: center; justify-content: center;
          background: var(--ot-ink); color: var(--ot-white);
          font-size: 12px; font-weight: 700; letter-spacing: 0.08em;
        }
        .ot-legend {
          flex: 1 1 400px; min-width: 0; margin: 0; padding: 0; list-style: none;
          display: flex; flex-direction: column;
        }
        .ot-legend__row { display: flex; gap: 24px; padding: 28px 0; border-top: 1px solid var(--ot-line); }
        .ot-legend__row:first-child { border-top-color: var(--ot-ink); }
        .ot-legend__row:last-child { border-bottom: 1px solid var(--ot-ink); }
        .ot-legend__n { flex: 0 0 36px; padding-top: 4px; font-size: 13px; font-weight: 700; letter-spacing: 0.12em; }
        .ot-legend__body { display: flex; flex-direction: column; gap: 8px; }
        .ot-legend__title { margin: 0; font-size: 22px; font-weight: 700; letter-spacing: -0.01em; }
        .ot-legend__text { margin: 0; max-width: 420px; font-size: 16px; line-height: 1.6; color: var(--ot-muted); }

        /* closing */
        .ot-close { background: var(--ot-ink); color: var(--ot-white); }
        .ot-close__inner {
          max-width: 1280px; margin: 0 auto; padding: 120px var(--ot-pad);
          display: flex; flex-wrap: wrap; justify-content: space-between; align-items: flex-end; gap: 48px;
        }
        .ot-close__copy { flex: 1 1 560px; min-width: 0; display: flex; flex-direction: column; gap: 32px; }
        .ot-close__title {
          margin: 0; font-size: clamp(40px, 5.4vw, 76px); font-weight: 800;
          line-height: 0.98; letter-spacing: -0.04em; text-transform: uppercase;
        }
        .ot-close__buy { flex: 0 1 340px; display: flex; flex-direction: column; gap: 20px; }
        .ot-close__row {
          display: flex; justify-content: space-between; align-items: baseline; gap: 16px;
          padding-top: 16px; border-top: 1px solid var(--ot-white);
          font-size: 12px; font-weight: 600; letter-spacing: 0.24em;
        }

        .ot-diagram--img { aspect-ratio: auto; padding: 0; }
        .ot-diagram__img { display: block; width: 100%; height: auto; }
        .ot-hero__sub { margin: 0; font-size: 16px; line-height: 1.6; color: var(--ot-muted); }
        .ot-scale__note { margin: 16px 0 0; font-size: 16px; line-height: 1.5; color: var(--ot-muted); }
        .ot-close__tag {
          margin: 0; font-size: 12px; font-weight: 500; letter-spacing: 0.28em;
          text-transform: uppercase; color: #bdbdbd;
        }

        /* unisex + in-house */
        .ot-make__inner {
          max-width: 1280px; margin: 0 auto; padding: 0 var(--ot-pad) 16px;
          display: flex; flex-wrap: wrap; gap: 16px;
        }
        .ot-make__cell {
          flex: 1 1 420px; min-width: 0; min-height: 360px; padding: 40px;
          display: flex; flex-direction: column; justify-content: space-between; gap: 32px;
          background: var(--ot-mist);
        }
        .ot-make__cell--ink { background: var(--ot-ink); color: var(--ot-white); }
        .ot-make__title {
          margin: 0; font-size: clamp(32px, 4vw, 56px); font-weight: 800;
          line-height: 0.98; letter-spacing: -0.03em; text-transform: uppercase;
        }
        .ot-make__text { margin: 0; max-width: 440px; font-size: 18px; line-height: 1.55; font-weight: 300; }

        @media (prefers-reduced-motion: reduce) {
          .ot-btn { transition: none; }
        }

        /* ══════════════════════════════════════════════
           TABLET + MOBILE  (≤ 900px) — compact layout
           ══════════════════════════════════════════════ */
        @media (max-width: 900px) {
          .ot-root { --ot-pad: 20px; }

          /* hero: title → image (full-bleed) → line + CTA */
          .ot-hero__inner { padding-top: 20px; }
          .ot-hero__meta-right { display: none; }
          .ot-hero__grid {
            flex-direction: column; flex-wrap: nowrap; gap: 16px; margin-top: 12px;
          }
          .ot-hero__copy { display: contents; }
          .ot-hero__title {
            order: 1; font-size: clamp(64px, 19vw, 120px);
          }
          .ot-hero__media {
            order: 2; flex: none; min-height: 0; aspect-ratio: 5 / 6;
            margin: 0 calc(-1 * var(--ot-pad)); border: 0;
          }
          .ot-hero__cta-block {
            order: 3; max-width: none; gap: 16px; padding-bottom: 28px;
          }
          .ot-hero__line { font-size: 18px; line-height: 1.35; }
          .ot-hero__cta-block .ot-btn { width: 100%; min-height: 50px; }
          .ot-hero__fig { bottom: 12px; font-size: 10px; }
          .ot-hero__fig--l { left: 16px; }
          .ot-hero__fig--r { right: 16px; }

          /* strip: one scrolling line */
          .ot-strip__inner {
            flex-wrap: nowrap; justify-content: flex-start; gap: 20px;
            padding: 14px var(--ot-pad); overflow-x: auto; scrollbar-width: none;
          }
          .ot-strip__inner::-webkit-scrollbar { display: none; }
          .ot-strip__item { gap: 20px; font-size: 11px; letter-spacing: 0.22em; white-space: nowrap; }

          /* statement: short */
          .ot-statement__inner { padding: 44px var(--ot-pad) 32px; gap: 16px; }
          .ot-statement__label { flex: 1 1 100%; padding-top: 0; }
          .ot-statement__big { font-size: clamp(22px, 6vw, 30px); line-height: 1.25; }
          .ot-statement__small { display: none; }

          /* bento: two compact tiles side by side */
          .ot-bento__inner {
            display: grid; grid-template-columns: 1fr 1fr; gap: 8px; padding-bottom: 8px;
          }
          .ot-tile {
            min-height: 0; padding: 16px; gap: 18px; justify-content: flex-start;
          }
          .ot-tile .ot-eyebrow { gap: 8px; align-items: flex-start; }
          .ot-tile .ot-eyebrow__line { flex-basis: 14px; width: 14px; margin-top: 6px; }
          .ot-tile .ot-eyebrow__text { font-size: 9px; letter-spacing: 0.16em; line-height: 1.3; }
          .ot-weight { gap: 6px; }
          .ot-weight__num { font-size: clamp(64px, 20vw, 110px); }
          .ot-weight__unit { font-size: 12px; letter-spacing: 0.16em; padding-bottom: 2px; }
          .ot-mix { gap: 12px; }
          .ot-mix__num { font-size: clamp(34px, 11vw, 60px); }
          .ot-mix__bar { height: 16px; }
          .ot-mix__labels { flex-direction: column; gap: 4px; font-size: 9px; letter-spacing: 0.14em; }
          .ot-tile__copy { font-size: 12.5px; line-height: 1.45; }

          /* weight scale: bars instead of side-scrolling ruler */
          .ot-scale__inner { padding-bottom: 8px; }
          .ot-scale__panel { padding: 20px 16px; }
          .ot-scale__scroll { display: none; }
          .ot-bars { display: flex; flex-direction: column; gap: 12px; margin-top: 20px; }

          /* anatomy: smaller diagram + 2x2 legend */
          .ot-anatomy__inner { padding: 48px var(--ot-pad) 56px; }
          .ot-anatomy__title {
            margin: 16px 0 24px; font-size: clamp(30px, 9vw, 44px);
          }
          .ot-anatomy__grid { gap: 20px; }
          .ot-diagram { flex: 1 1 100%; max-width: 260px; margin: 0 auto; }
          .ot-diagram__marker { width: 26px; height: 26px; font-size: 11px; }
          .ot-legend {
            flex: 1 1 100%; display: grid; grid-template-columns: 1fr 1fr; column-gap: 16px;
          }
          .ot-legend__row {
            flex-direction: column; gap: 6px; padding: 14px 0;
          }
          .ot-legend__row:nth-child(2) { border-top-color: var(--ot-ink); }
          .ot-legend__row:last-child { border-bottom: 0; }
          .ot-legend__n { flex: none; padding-top: 0; font-size: 11px; }
          .ot-legend__body { gap: 4px; }
          .ot-legend__title { font-size: 15px; }
          .ot-legend__text { font-size: 12.5px; line-height: 1.45; }

          .ot-diagram--img { max-width: none; margin: 0; }

          /* hero sub + scale note */
          .ot-hero__sub { font-size: 14px; line-height: 1.5; }
          .ot-hero__cta-block { gap: 12px; }
          .ot-scale__note { font-size: 13px; margin-top: 10px; }

          /* unisex + in-house */
          .ot-make__inner { padding-bottom: 8px; gap: 8px; }
          .ot-make__cell { flex: 1 1 100%; min-height: 0; padding: 22px 20px; gap: 14px; }
          .ot-make__title { font-size: clamp(26px, 8vw, 36px); }
          .ot-make__text { font-size: 14px; line-height: 1.5; }
          .ot-close__tag { font-size: 10px; letter-spacing: 0.2em; }

          /* closing */
          .ot-close__inner { padding: 52px var(--ot-pad); gap: 28px; }
          .ot-close__copy { flex: 1 1 100%; gap: 18px; }
          .ot-close__title { font-size: clamp(32px, 10vw, 52px); }
          .ot-close__buy { flex: 1 1 100%; gap: 14px; }
        }

        /* ══════════════════════════════════════════════
           FEATURED PRODUCTS GRID  (prefix: fcs-) — from original
           ══════════════════════════════════════════════ */
        .fcs-root {
          background: #f8f8f8;
          padding: 80px 0 72px;
          font-family: 'Exo 2', sans-serif;
        }

        /* ── HEADER ── */
        .fcs-header {
          max-width: 1400px;
          margin: 0 auto 40px;
          padding: 0 48px;
          display: flex;
          align-items: flex-end;
          justify-content: space-between;
        }

        .fcs-eyebrow {
          font-size: 9px;
          font-weight: 400;
          letter-spacing: 0.5em;
          text-transform: uppercase;
          color: rgba(0,0,0,0.4);
          margin-bottom: 12px;
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .fcs-eyebrow::before {
          content: '';
          width: 24px;
          height: 1px;
          background: rgba(0,0,0,0.2);
          display: block;
          flex-shrink: 0;
        }

        .fcs-title {
          font-size: clamp(28px, 4vw, 56px);
          font-weight: 200;
          color: #000;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          line-height: 1;
          margin: 0;
        }
        .fcs-title span { font-weight: 600; }

        .fcs-cta-header {
          font-size: 9px;
          font-weight: 500;
          letter-spacing: 0.42em;
          text-transform: uppercase;
          color: #000;
          text-decoration: none;
          position: relative;
          padding-bottom: 6px;
          white-space: nowrap;
          flex-shrink: 0;
        }
        .fcs-cta-header::after {
          content: '';
          position: absolute;
          bottom: 0; left: 0;
          width: 100%;
          height: 1px;
          background: rgba(0,0,0,0.25);
          transition: background 0.25s ease;
        }
        .fcs-cta-header:hover::after { background: #000; }

        /* ── RULE ── */
        .fcs-rule {
          max-width: 1400px;
          margin: 0 auto 36px;
          padding: 0 48px;
        }
        .fcs-rule hr {
          border: none;
          border-top: 1px solid rgba(0,0,0,0.08);
        }

        /* ── GRID ── */
        .fcs-grid {
          max-width: 1400px;
          margin: 0 auto;
          padding: 0 48px;
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 2px;
        }

        /* ── SKELETON ── */
        .fcs-skeleton {
          background: #e8e8e8;
          aspect-ratio: 3 / 4;
          position: relative;
          overflow: hidden;
        }
        .fcs-skeleton::after {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.6) 50%, transparent 100%);
          animation: fcs-shimmer 1.6s infinite;
        }
        @keyframes fcs-shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }

        /* ── CARD ── */
        .fcs-card {
          position: relative;
          background: #f8f8f8;
          overflow: hidden;
        }

        .fcs-card-link {
          display: block;
          text-decoration: none;
        }

        .fcs-img-wrap {
          width: 100%;
          aspect-ratio: 3 / 4;
          overflow: hidden;
        }

        .fcs-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
          transition: transform 0.7s cubic-bezier(0.25, 0.46, 0.45, 0.94);
        }
        .fcs-card:hover .fcs-img { transform: scale(1.04); }

        /* Caption — no background */
        .fcs-caption {
          padding: 10px 2px 14px;
          display: flex;
          align-items: baseline;
          justify-content: space-between;
          gap: 8px;
        }

        .fcs-name {
          font-size: 10px;
          font-weight: 500;
          letter-spacing: 0.22em;
          text-transform: uppercase;
          color: #000;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .fcs-price {
          font-size: 12px;
          font-weight: 300;
          letter-spacing: 0.1em;
          color: rgba(0,0,0,0.5);
          white-space: nowrap;
          flex-shrink: 0;
        }

        /* ── RESPONSIVE ── */
        @media (max-width: 1024px) {
          .fcs-grid { grid-template-columns: repeat(2, 1fr); padding: 0 24px; }
          .fcs-header { padding: 0 24px; }
          .fcs-rule { padding: 0 24px; }
        }

        @media (max-width: 768px) {
          .fcs-root { padding: 52px 0 48px; }
          .fcs-header { flex-direction: row; align-items: flex-end; padding: 0 16px; margin-bottom: 24px; }
          .fcs-title { font-size: clamp(22px, 7vw, 32px); }
          .fcs-eyebrow { font-size: 8px; margin-bottom: 8px; }
          .fcs-rule { padding: 0 16px; margin-bottom: 20px; }
          .fcs-grid { grid-template-columns: repeat(2, 1fr); padding: 0 16px; gap: 8px; }
          .fcs-caption { padding: 8px 0 10px; flex-direction: column; gap: 2px; }
          .fcs-name { font-size: 9px; letter-spacing: 0.15em; }
          .fcs-price { font-size: 11px; }
          .fcs-cta-header { font-size: 8px; letter-spacing: 0.3em; }
        }

        @media (max-width: 360px) {
          .fcs-grid { gap: 6px; padding: 0 12px; }
          .fcs-header { padding: 0 12px; }
        }
      `}</style>

      <div className="ot-root">
        {/* HERO */}
        <section className="ot-hero" aria-labelledby="ot-hero-title">
          <div className="ot-hero__inner">
            <div className="ot-hero__meta">
              <OtEyebrow>FEATURED COLLECTION — Nº 01</OtEyebrow>
              <span className="ot-hero__meta-right">TERRY OVERSIZED TEE</span>
            </div>

            <div className="ot-hero__grid">
              <div className="ot-hero__copy">
                <h1 id="ot-hero-title" className="ot-hero__title">
                  <span>Over</span>
                  <span>Sized.</span>
                </h1>
                <div className="ot-hero__cta-block">
                  <p className="ot-hero__line">Some mornings deserve to be dressed for.</p>
                  <p className="ot-hero__sub">
                    Heavy cotton terry, cut wide and soft on the inside. The one you reach for first and
                    take off last.
                  </p>
                  <div>
                    <a className="ot-btn ot-btn--dark" href="#fcs-collection">
                      Shop the tee
                    </a>
                  </div>
                </div>
              </div>

              <div className="ot-hero__media">
                {HERO_IMAGE ? (
                  <img className="ot-hero__img" src={HERO_IMAGE} alt={HERO_ALT} />
                ) : (
                  <div className="ot-hero__placeholder">
                    <span className="ot-hero__ph-tag">[ HIGHLIGHT IMAGE ]</span>
                    <span className="ot-hero__ph-note">
                      Black Terry Oversized Tee, single model, seamless studio on #f8f8f8
                    </span>
                  </div>
                )}
                <span className="ot-hero__fig ot-hero__fig--l">FIG. 01</span>
                <span className="ot-hero__fig ot-hero__fig--r">260 GSM</span>
              </div>
            </div>
          </div>
        </section>

        {/* STRIP */}
        <section className="ot-strip" aria-label="Key specifications">
          <ul className="ot-strip__inner">
            {MARQUEE.map((item, i) => (
              <li key={item} className="ot-strip__item">
                {i > 0 && <span className="ot-strip__dot" aria-hidden="true" />}
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* STATEMENT */}
        <section className="ot-statement">
          <div className="ot-statement__inner">
            <div className="ot-statement__label">
              <OtEyebrow>THE SPEC SHEET</OtEyebrow>
            </div>
            <div className="ot-statement__body">
              <p className="ot-statement__big">
                You'll feel it before anyone sees it. The weight settles on your shoulders, the inside is soft
                against your skin, and the fit asks nothing of you.
              </p>
              <p className="ot-statement__small">
                Seven details, each chosen for one thing: how it feels on you.
              </p>
            </div>
          </div>
        </section>

        {/* BENTO */}
        <section className="ot-bento">
          <div className="ot-bento__inner">
            <article className="ot-tile ot-tile--ink ot-tile--weight">
              <OtEyebrow invert>01 — THE WEIGHT</OtEyebrow>
              <div className="ot-weight">
                <span className="ot-weight__num">260</span>
                <span className="ot-weight__unit">GSM</span>
              </div>
              <p className="ot-tile__copy">
                Heavy cotton terry. It drapes instead of clinging, so you look put-together without trying.
              </p>
            </article>

            <article className="ot-tile ot-tile--mist ot-tile--mix">
              <OtEyebrow>02 — THE BLEND</OtEyebrow>
              <div className="ot-mix">
                <span className="ot-mix__num">90 / 10</span>
                <div
                  className="ot-mix__bar"
                  role="img"
                  aria-label="90 percent cotton, 10 percent polyester"
                >
                  <div className="ot-mix__cotton" />
                  <div className="ot-mix__poly" />
                </div>
                <div className="ot-mix__labels">
                  <span>90% COTTON</span>
                  <span className="ot-mix__labels-muted">10% POLYESTER</span>
                </div>
              </div>
              <p className="ot-tile__copy">
                Cotton for softness against your skin. A touch of polyester to help it hold its shape, day after day.
              </p>
            </article>
          </div>
        </section>

        {/* WEIGHT SCALE */}
        <section className="ot-scale">
          <div className="ot-scale__inner">
            <div className="ot-scale__panel">
              <OtEyebrow>03 — WHERE 260 SITS IN THE BLANK</OtEyebrow>
              <p className="ot-scale__note">
                Lighter tees fill the rest of the range. This is the one with presence.
              </p>

              {/* desktop ruler */}
              <div className="ot-scale__scroll">
                <div className="ot-scale__track">
                  <div className="ot-scale__axis" aria-hidden="true" />
                  {SCALE.map((s) => (
                    <div
                      key={s.gsm}
                      className={`ot-scale__tick${s.active ? " ot-scale__tick--active" : ""}`}
                      style={{ left: `${scalePos(s.gsm)}%` }}
                    >
                      {s.active ? <span className="ot-scale__marker" aria-hidden="true" /> : null}
                      <span className="ot-scale__stem" aria-hidden="true" />
                      <span className="ot-scale__gsm">{s.gsm}</span>
                      <span className="ot-scale__name">{s.label}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* mobile bars */}
              <ul className="ot-bars">
                {SCALE.map((s) => (
                  <li key={s.gsm} className={`ot-bar${s.active ? " ot-bar--active" : ""}`}>
                    <div className="ot-bar__top">
                      <span>{s.label}</span>
                      <span className="ot-bar__gsm">{s.gsm}</span>
                    </div>
                    <div className="ot-bar__track" aria-hidden="true">
                      <div
                        className="ot-bar__fill"
                        style={{ width: `${(s.gsm / SCALE_TOP) * 100}%` }}
                      />
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* ANATOMY */}
        <section className="ot-anatomy">
          <div className="ot-anatomy__inner">
            <OtEyebrow>04 — ANATOMY</OtEyebrow>
            <h2 className="ot-anatomy__title">The details you can feel.</h2>

            <div className="ot-anatomy__grid">
              <div className={`ot-diagram${ANATOMY_IMAGE ? " ot-diagram--img" : ""}`}>
                {ANATOMY_IMAGE ? (
                  <img className="ot-diagram__img" src={ANATOMY_IMAGE} alt={ANATOMY_ALT} loading="lazy" />
                ) : (
                <svg viewBox="0 0 400 400" className="ot-diagram__svg" aria-hidden="true">
                  <path
                    d="M130 50 L170 38 Q200 62 230 38 L270 50 L372 118 L336 176 L292 152 L292 352 L108 352 L108 152 L64 176 L28 118 Z"
                    fill="#ffffff"
                    stroke="#0a0a0a"
                    strokeWidth="2"
                    strokeLinejoin="miter"
                  />
                  <path d="M170 38 Q200 62 230 38" fill="none" stroke="#0a0a0a" strokeWidth="7" />
                  <path d="M100 70 L108 152" fill="none" stroke="#0a0a0a" strokeWidth="1.5" strokeDasharray="5 4" />
                  <path d="M300 70 L292 152" fill="none" stroke="#0a0a0a" strokeWidth="1.5" strokeDasharray="5 4" />
                  <path d="M108 336 L292 336" fill="none" stroke="#0a0a0a" strokeWidth="1.5" strokeDasharray="5 4" />
                </svg>
                )}
                {(!ANATOMY_IMAGE || SHOW_MARKERS) && ANATOMY.map((a) => (
                  <span
                    key={a.n}
                    className="ot-diagram__marker"
                    style={{ left: `${a.left}%`, top: `${a.top}%` }}
                    aria-hidden="true"
                  >
                    {a.n}
                  </span>
                ))}
              </div>

              <ol className="ot-legend">
                {ANATOMY.map((a) => (
                  <li key={a.n} className="ot-legend__row">
                    <span className="ot-legend__n">{a.n}</span>
                    <div className="ot-legend__body">
                      <h3 className="ot-legend__title">{a.title}</h3>
                      <p className="ot-legend__text">{a.text}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </section>

        {/* UNISEX + IN-HOUSE */}
        <section className="ot-make">
          <div className="ot-make__inner">
            <article className="ot-make__cell">
              <OtEyebrow>05 — UNISEX STYLE</OtEyebrow>
              <h3 className="ot-make__title">One cut. Every build.</h3>
              <p className="ot-make__text">
                Made for everyone. It fits the way you wear it, not the way a label says you should.
              </p>
            </article>
            <article className="ot-make__cell ot-make__cell--ink">
              <OtEyebrow invert>06 — MADE IN-HOUSE</OtEyebrow>
              <h3 className="ot-make__title">Stitched with intent.</h3>
              <p className="ot-make__text">
                Sewn in-house on state-of-the-art machines, from premium 260 GSM cotton terry. Made
                carefully, so it sits right and holds up.
              </p>
            </article>
          </div>
        </section>

        {/* CLOSING */}
        <section className="ot-close">
          <div className="ot-close__inner">
            <div className="ot-close__copy">
              <OtEyebrow invert>VESHH-EON</OtEyebrow>
              <p className="ot-close__title">
                Put it on.
                <br />
                Feel the difference.
              </p>
              <p className="ot-close__tag">Built by one. Meant for a billion.</p>
            </div>
            <div className="ot-close__buy">
              <div className="ot-close__row">
                <span>OVERSIZED T-SHIRTS</span>
              </div>
              <a className="ot-btn ot-btn--light" href="#fcs-collection">
                Shop the collection
              </a>
            </div>
          </div>
        </section>
      </div>

      {/* ═════════ FEATURED PRODUCTS (from original) ═════════ */}
      <section className="fcs-root" id="fcs-collection">
        {/* HEADER */}
        <div className="fcs-header">
          <div>
            <div className="fcs-eyebrow">Featured Category</div>
            <h2 className="fcs-title">Oversized <span>T-Shirts</span></h2>
          </div>
          <Link to={COLLECTION_PATH} className="fcs-cta-header">
            Shop All
          </Link>
        </div>

        {/* DIVIDER */}
        <div className="fcs-rule"><hr /></div>

        {/* GRID */}
        <div className="fcs-grid">
          {loading
            ? [...Array(8)].map((_, i) => <div key={i} className="fcs-skeleton" />)
            : displayProducts.map((product) => (
                <div key={product._id} className="fcs-card">
                  <Link to={`/product/${product._id}`} className="fcs-card-link">
                    <div className="fcs-img-wrap">
                      <img
                        src={product.images?.[0]?.url}
                        alt={product.name || "Product"}
                        className="fcs-img"
                        draggable="false"
                      />
                    </div>
                    <div className="fcs-caption">
                      <span className="fcs-name">{product.name}</span>
                      <span className="fcs-price">₹{product.price}</span>
                    </div>
                  </Link>
                </div>
              ))}
        </div>
      </section>
    </>
  );
};

export default FeaturedOverSizedShirt;
